import React from 'react';
import Spinner from 'react-bootstrap/Spinner';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import BootstrapTable from 'react-bootstrap-table-next';
import { Link  } from 'react-router-dom';
import paginationFactory from 'react-bootstrap-table2-paginator';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import { DataContext } from './DataContextProvider.js';

export default function DataTable({item, title, filters, paginate, extra}) {
  const dataContext = React.useContext(DataContext);
  const dataAvailable = dataContext.getItemData({
    start: dataContext.start,
    end: dataContext.end,
    rows: item,
    filters: filters,
  });

  const columns = [
    {
      dataField: 'rank',
      text: '#',
      sort: false,
      align: 'right',
    },
    {
      dataField: 'link',
      text: title,
      sort: true,
      sortFunc: (a, b, order, dataField, rowA, rowB) => {
        let r = 0;
        if (rowA.name < rowB.name)
          r = -1;
        else if (rowB.name < rowA.name)
          r = 1;
        return (order === 'asc') ? r : r * -1;
      },
      align: 'left',
      headerStyle: (col, idx) => { return {width: '100%'} },
      filter: textFilter({ placeholder: 'Search' }),
      filterValue: (cell, row) => row.name,
    },
    {
      dataField: 'page_views',
      text: 'Views',
      sort: true,
      align: 'right',
    },
    {
      dataField: 'organic_views',
      text: 'Organic',
      sort: true,
      align: 'right',
    },
    {
      dataField: 'direct_views',
      text: 'Direct',
      sort: true,
      align: 'right',
    },
    {
      dataField: 'paid_views',
      text: 'Paid',
      sort: true,
      align: 'right',
    },
    {
      dataField: 'other_views',
      text: 'Other',
      sort: true,
      align: 'right',
    },
    /*
    {
      dataField: 'sessions',
      text: 'Sessions',
      sort: true,
      align: 'right',
    },
    {
      dataField: 'users',
      text: 'Users',
      sort: true,
      align: 'right',
    },
    */
    {
      dataField: 'signups',
      text: (
        <>
          Signups{' '}
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Sign Up clicks from CTA on page</Tooltip>}
          >
            <span style={{ cursor: 'help' }}>ⓘ</span>
          </OverlayTrigger>
        </>
      ),
      sort: true,
      align: 'right',
    },
    {
      dataField: 'conversion_rate',
      text: '% Rate',
      sort: true,
      align: 'right',
    },
  ];
  if (item === 'articles') {
    if (dataContext.blog !== 'docs') {
      columns.splice(2, 0, {
        dataField: 'date',
        text: 'Date',
        sort: true,
        align: 'right',
        headerStyle: (col, idx) => { return {minWidth: '85px'} },
      });
    }
  }
  else if (item !== 'countries') {
    columns.splice(2, 0, {
      dataField: 'count',
      text: 'Posts',
      sort: true,
      align: 'right',
    });
  }

  let data = [];
  if (dataAvailable) {
    data = dataContext.itemData.results.map(i => {
      const doc_prefix = {doc: '/docs/', dsg: 'docs.sendgrid.com/'};
      let result = {
        id: i[0][0],
        name: i[0][1],
        link: (i[0][0] !== '') ?
          <>
            <Link to={`/${item}/` + i[0][0]}>{i[0][1]}</Link>
            {item === 'articles' && i[9] > 1 &&
              <> <code>(+{i[9] - 1})</code></>
            }
            
            {i[0].length > 3 && (i[0][3].startsWith('doc!') || i[0][3].startsWith('dsg!')) &&
              <>
                <br />
                <small><i>{doc_prefix[i[0][3].substring(0, 3)]}{i[0][3].substring(4)}</i></small>
              </>
            }
            
          </> : i[0][1],
        rank: 0,
        date: i[0][2],
        users: i[1],
        sessions: i[2],
        page_views: i[3],
        organic_views: i[4],
        direct_views: i[5],
        paid_views: i[6],
        other_views: i[3] - i[4] - i[5] - i[6],
        signups: i[7],
        conversion_rate: parseInt(i[8] * 100 + 0.5) / 100,
        count: i[9],
      };
      if (extra) {
        // i[0][3] is the article slug, which starts with sg! for SendGrid content
        result.link = extra(i[0][0], i[0][3] && i[0][3].startsWith('sg!'), result.link);
      }
      return result;
    });
  }

  const updateRanks = (field) => {
    const numericSort = (a, b) => {
      const sa = a[field] || '';
      const sb = b[field] || '';
      return (sa === sb) ? b.name.localeCompare(a.name) : ((sa > sb) ? -1 : 1);
    };
    const stringSort = (a, b) => {
      const sa = a[field] || '';
      const sb = b[field] || '';
      return (sa === sb) ? b.name.localeCompare(a.name) : sb.localeCompare(sa);
    };

    if (field !== 'date') {
      data = data.sort(numericSort);
    }
    else {
      data = data.sort(stringSort);
    }
    //data = data.sort((a, b) => (a[field] === b[field]) ? 0 : ((a[field] > b[field] ? -1 : 1)));
    for (let i = 1; i <= data.length; i++) {
      data[i - 1].rank = i;
    }
  };

  React.useEffect(() => {
    let searchBox = document.getElementById('text-filter-column-link');
    if (searchBox)
      searchBox.type = 'search';
  }, [dataAvailable]);
  for (let i = 0; i < columns.length; i++) {
    columns[i].onSort = (field, order) => {
      sessionStorage.setItem('tableSortField', field);
      sessionStorage.setItem('tableSortOrder', order);
      updateRanks(field);
    }
  }

  let sortField = sessionStorage.getItem('tableSortField') || 'page_views';
  let sortOrder = sessionStorage.getItem('tableSortOrder') || 'desc';
  const column_fields = columns.map(c => c.dataField);
  if (column_fields.indexOf(sortField) === -1) {
    sortField = 'page_views';
    sortOrder = 'desc';
  }
  updateRanks(sortField);

  const paginationOptions = {
    sizePerPageList: [10, 25, 50, 100],
    sizePerPage: sessionStorage.getItem('tablePageSize') || 10,
    onSizePerPageChange: (sizePerPage) => {
      sessionStorage.setItem('tablePageSize', sizePerPage);
    },
  };

  return (
    <div className="card data-table">
      { dataAvailable ?
        <BootstrapTable
          bootstrap4
          keyField='id'
          data={data}
          columns={columns}
          pagination={paginate ? paginationFactory(paginationOptions) : undefined}
          defaultSorted={[{ dataField: sortField, order: sortOrder }]}
          filter={filterFactory()}
          striped
          hover
          condensed
        />
      :
        <Spinner animation="grow" />
      }
    </div>
  );
}
